import PaymentIcon from "@mui/icons-material/Payment";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import {
  BoolBadge,
  DetailRow,
  DetailsBlock,
  formatDate,
  MetricTile,
  PanelCard,
  SectionHeader,
} from "../components/accountUi";
import type { PaymentData } from "../accountTypes";

type SubscriptionTabPanelProps = {
  payment: PaymentData | null;
  onManagePayment: () => void;
};

export default function SubscriptionTabPanel({
  payment,
  onManagePayment,
}: SubscriptionTabPanelProps) {
  const isActive = payment?.active ?? true;
  const autoRenewOn = Boolean(payment?.recurring && isActive);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <MetricTile
          label="Current package"
          value={payment?.packageName || "—"}
          accent
        />
        <MetricTile
          label="Monthly rental"
          value={
            payment?.price != null && payment.price !== ""
              ? `LKR ${payment.price}`
              : "—"
          }
        />
        <MetricTile
          label={autoRenewOn ? "Next billing / ends" : "Subscription ends"}
          value={formatDate(payment?.paymentendDate)}
        />
        <MetricTile
          label="Auto-renew"
          value={<BoolBadge value={autoRenewOn} />}
        />
      </div>

      <div className="xl:grid xl:grid-cols-12 xl:gap-4 xl:items-stretch">
        <PanelCard className="p-4 sm:p-5 xl:col-span-7">
          <SectionHeader icon={PaymentIcon} title="Plan details" />
          <div className="xl:grid xl:grid-cols-2 xl:gap-4">
            <DetailsBlock>
              <DetailRow label="Period start" value={formatDate(payment?.startDate)} />
              <DetailRow label="Period end" value={formatDate(payment?.endDate)} />
              <DetailRow
                label="Subscription ends"
                value={formatDate(payment?.paymentendDate)}
              />
            </DetailsBlock>
            <DetailsBlock>
              <DetailRow label="Free package" value={<BoolBadge value={payment?.free} />} />
              <DetailRow label="Active subscription" value={<BoolBadge value={isActive} />} />
              <DetailRow label="Auto-renew (PayHere)" value={<BoolBadge value={autoRenewOn} />} />
            </DetailsBlock>
          </div>
          {autoRenewOn ? (
            <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
              Monthly charges are billed automatically by PayHere using the card on file.
              To stop auto-renewal, cancel the subscription from your PayHere account;
              this app will deactivate when PayHere notifies us.
            </p>
          ) : (
            <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
              Auto-renew is off. Use Manage subscription to set up monthly billing via PayHere.
            </p>
          )}
        </PanelCard>

        <div className="flex flex-col justify-center rounded-2xl border border-dashed border-primary/30 bg-gradient-to-br from-primary/[0.06] to-primaryLight/5 p-4 xl:col-span-5 dark:border-primary/40 dark:from-primary/10 dark:to-slate-800/40">
          {autoRenewOn ? (
            <AutorenewIcon sx={{ fontSize: 36 }} className="text-primary mb-3" />
          ) : (
            <PaymentIcon sx={{ fontSize: 36 }} className="text-primary mb-3" />
          )}
          <p className="text-lg font-bold text-slate-900 dark:text-white">
            Manage billing & payments
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 mb-5">
            {autoRenewOn
              ? "Auto-renewal is active via PayHere. Renew early or view billing details."
              : "Set up monthly auto-renewal via PayHere, view invoices, or manage your subscription."}
          </p>
          <button
            type="button"
            onClick={onManagePayment}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-white shadow-md shadow-primary/25 transition hover:opacity-95 hover:shadow-lg hover:shadow-primary/30 sm:w-auto"
          >
            <PaymentIcon sx={{ fontSize: 18 }} />
            Manage subscription
          </button>
        </div>
      </div>
    </div>
  );
}
